// The `billing.*` namespace: the wallet's own screens, the cost disclosures, the price catalogue
// and the wallet label the home card reads. The wallet's screens also read
// `src/apps/settings/locales/*.billing.js`.
export default {
  // Two words these screens need and `core.common` does not carry.
  common: {
    loadMore: 'Charger la suite',
    done: 'Terminé',
  },
  // Every price inside the app is excluding tax and says so (`docs/plans/billing-ui.md` U2):
  // `composables/useMoney.ts`. The mention sits before the unit in French.
  money: {
    exclTax: '{amount} HT',
    rateExclTax: '{amount} HT{unit}',
    perMonth: '/ mois',
    // A monthly estimate (`composables/useMonthlyEstimate.ts`): `{amount}` already says HT. Under
    // half a cent reads « < 0,01 € HT / mois » rather than a « 0,00 € » that would read as free.
    approxMonthly: '≈ {amount} / mois',
    underMonthly: '< {amount} / mois',
    // A price of zero: what the organization was offered (`useMoney`'s `priceOrOffered`).
    offered: 'Offert',
  },
  // The balance check (`components/BalanceCheck.vue`): its generic words, which a caller replaces
  // with its own subject's.
  balance: {
    label: 'Votre solde',
    missing: 'Il manque {amount}.',
    exempt: 'Exonéré : rien n’est prélevé.',
    contract: 'Compris dans votre contrat : rien n’est prélevé ici.',
  },
  // The inline top-up (`components/InlineTopUp.vue`). Two keys for the months rather than
  // vue-i18n's plural — « mois » is invariable, « couvert » is not.
  inlineTopUp: {
    title: 'Combien voulez-vous recharger ?',
    recommended: 'Recommandé',
    receive: 'Vous recevez {amount}',
    monthOne: '≈ 1 mois couvert',
    months: '≈ {months} mois couverts',
  },
  // The « insufficient credits » modal (`modals/CreditsExhaustedModal.vue`), where every
  // `402 credits_exhausted` ends. The member's version names who can top up.
  creditsExhausted: {
    title: 'Crédits insuffisants',
    lede: 'Rien n’a été lancé ni débité. Rechargez votre portefeuille, puis relancez l’action.',
    ledeRetry: 'Rien n’a été lancé ni débité. Rechargez, et l’action est relancée dès que le crédit est arrivé.',
    costLabel: 'Ce que coûte l’action',
    notEnough: 'Votre solde ne couvre pas cette action.',
    neverFunded: 'Vous n’avez encore rien rechargé : le crédit acheté paie les sous-titres, le stockage, la diffusion et vos abonnements.',
    coversNow: 'Votre solde couvre maintenant cette action.',
    fineprint: 'Montants HT. La TVA est ajoutée à l’étape du paiement.',
    topUpTitle: 'Recharger le portefeuille',
    topUp: 'Recharger {amount}',
    topUpAndRetry: 'Recharger {amount} et relancer',
    retry: 'Relancer',
    understood: 'Compris',
    credited: 'Le crédit est arrivé : vous pouvez relancer l’action.',
    creditPending: 'Paiement accepté. Le crédit met parfois une minute à arriver — l’action est relancée dès qu’il est là.',
    creditLate: 'Votre paiement est bien passé, mais le crédit n’est pas encore arrivé. Réessayez dans un instant.',
    memberTitle: 'Le solde de l’organisation ne suffit pas',
    memberLede: 'Rien n’a été lancé. Seuls les responsables de la facturation peuvent recharger le portefeuille : demandez à l’un d’eux.',
    memberLedeCost: 'Cette action coûte {amount}. Rien n’a été lancé. Seuls les responsables de la facturation peuvent recharger le portefeuille : demandez à l’un d’eux.',
    managersLabel: 'Responsables de la facturation',
    managersMore: 'et {count} autre(s)',
  },
  topUp: {
    title: 'Recharger le portefeuille',
    open: 'Recharger',
    lede: 'Choisissez un montant. Les crédits arrivent sur le portefeuille dès que le paiement est encaissé.',
    bonus: '+ {amount} offerts',
    noBonus: 'Sans bonus',
    customLabel: 'Autre montant (€ HT)',
    customHint: 'Entre {min} et {max}',
    confirm: 'Continuer',
    opening: 'Ouverture de la recharge…',
    paid: 'Paiement encaissé : les crédits sont sur votre portefeuille.',
    pending:
      'La recharge est ouverte et le paiement n’est pas encore confirmé. Elle reste visible dans vos recharges, où vous pourrez en vérifier l’état.',
    seeHistory: 'Voir mes recharges',
    uncollectable: {
      title: 'Le paiement ne peut pas être encaissé ici',
      body:
        'La recharge est bien ouverte et le service de facturation attend le règlement, mais il n’a pas transmis ce dont cette page a besoin pour confirmer le paiement. Rien n’a été débité. La recharge reste listée dans vos recharges : vous pouvez y suivre son état et la vérifier.',
    },
    payTitle: 'Régler une recharge',
    // The wallet has the credits of a debit being collected (`incoming_cents`, bonus included).
    incoming:
      'Un prélèvement est déjà en cours : {amount} de crédits arrivent dès sa confirmation par votre banque. Cette recharge sera un paiement supplémentaire.',
    keepsCard:
      'La carte avec laquelle vous payez est gardée pour l’auto-recharge, et remplace celle gardée auparavant.',
    legalAccept: 'En payant, vous acceptez les {terms} et les {use}.',
    termsOfSale: 'CGV',
    termsOfUse: 'CGU',
    payment: {
      loading: 'Chargement du formulaire de paiement…',
      pay: 'Payer {amount}',
      payUnknown: 'Payer',
      waiting: 'Paiement accepté — en attente de l’arrivée des crédits sur votre portefeuille…',
      // The invoicing service says a direct debit of this top-up is being collected.
      collecting:
        'Votre paiement est enregistré et votre banque est en train de le traiter. Vos crédits seront ajoutés automatiquement dès sa confirmation — pour un prélèvement SEPA, comptez en moyenne 8 jours. Vous n’avez rien d’autre à faire : la recharge reste visible dans votre historique, marquée « Prélèvement en cours ».',
      // Every other « not yet »: a debit not recorded yet, a card whose confirmation is late.
      processing:
        'Votre paiement est en cours de confirmation. Vos crédits seront ajoutés automatiquement dès qu’il sera confirmé, et la recharge reste visible dans votre historique.',
      // Shown under the payment form while SEPA Direct Debit is the method picked.
      sepaNotice:
        'Avec le prélèvement SEPA, vos crédits sont ajoutés une fois le prélèvement confirmé par votre banque, ce qui prend en moyenne 8 jours. Pour des crédits disponibles immédiatement, payez par carte.',
      requiresAction:
        'Votre banque a demandé une confirmation qui n’a pas été menée à bout. Rien n’a été débité : vous pouvez réessayer.',
      declined: 'Le paiement a été refusé',
      tryAgain: 'Réessayer',
      scriptUnavailable:
        'Le formulaire de paiement n’a pas pu être chargé. Vérifiez que rien ne bloque js.stripe.com, puis réessayez.',
    },
    failed: {
      title: 'La recharge a échoué',
      requestId: 'Référence à citer : {id}',
      purchaseSurvives:
        'La recharge avait déjà été ouverte : elle apparaît dans vos recharges et rien n’a été débité. Elle se ferme d’elle-même à son échéance.',
      startAgain: 'Recommencer',
    },
    recap: {
      amount: 'Montant',
      bonus: 'Bonus de recharge',
      credited: 'Crédité sur le portefeuille',
      taxNote: 'Montants hors taxes. La TVA est calculée par le service de facturation, et le montant TTC affiché à l’étape du paiement.',
    },
  },
  // The billing identity's step in the top-up; its fields are `settings.billing.identity.*`.
  identity: {
    intro: 'Ces informations figurent sur vos factures. Vous ne les saisissez qu’une fois.',
    back: 'Retour',
  },
  autoTopUp: {
    title: 'Auto-recharge',
    on: 'Activée',
    off: 'Désactivée',
    lede:
      'Votre solde ne tombe jamais à zéro : sous le seuil que vous choisissez, on recharge pour vous, sur la carte gardée.',
    sentenceStart: 'Recharger de',
    sentenceMiddle: 'HT dès que le solde descend sous',
    cardTitle: 'Moyen de paiement',
    cardSavedCard: 'Carte enregistrée : celle de votre dernière recharge où vous l’avez gardée.',
    cardSaved: 'Enregistré : {kind}, celui de votre dernière recharge où vous l’avez gardé.',
    cardUnknown: 'Impossible de vérifier pour le moment : le service de facturation ne répond pas.',
    cardNone: 'Aucune carte enregistrée. Une recharge payée en gardant la carte l’enregistre.',
    fineprint: 'La TVA est ajoutée au débit. Vous pouvez désactiver l’auto-recharge à tout moment.',
    noCard:
      'Aucune carte n’est gardée pour l’auto-recharge : elle ne peut rien débiter. Gardez-en une en réglant une recharge avec — sinon elle se désactive à la prochaine recharge qu’elle ne peut pas payer.',
    kinds: {
      card: 'carte',
      direct_debit: 'compte bancaire (prélèvement)',
      wallet: 'portefeuille électronique',
      bank_transfer: 'virement',
      other: 'moyen de paiement',
    },
    failures:
      'Les {count} dernière(s) auto-recharge(s) ont été refusées par votre banque. À {max} refus d’affilée, elle se désactive.',
    keepCard: 'Garder une carte',
    replaceCard: 'Utiliser une autre carte',
    keepCardTitle: 'Garder une carte pour l’auto-recharge',
    switchedOffAfterFailures:
      'L’auto-recharge s’est désactivée après {max} paiements refusés d’affilée. La réactiver vous demandera de l’accepter à nouveau.',
    thresholdLabel: 'Recharger quand le solde passe sous (€ HT)',
    amountLabel: 'Montant de chaque recharge (€ HT)',
    problems: {
      threshold: 'Le seuil est un solde de zéro ou plus.',
      amount: 'Une recharge est comprise entre {min} et {max}.',
    },
    consent:
      'J’accepte que la carte que je garde pour cela soit débitée de {amount} HT, TVA en sus, chaque fois que le solde passe sous {threshold}, sans que cela me soit demandé à chaque fois. Je peux la désactiver à tout moment.',
    switchOn: 'Activer',
    save: 'Enregistrer',
  },
  purchases: {
    status: {
      open: 'En attente',
      completed: 'Créditée',
      expired: 'Expirée',
      cancelled: 'Annulée',
      // Not a backend word: an open top-up whose last attempt was refused (`status.ts`).
      declined: 'Paiement refusé',
      // An open top-up whose bank asked for 3-D Secure: not a refusal (`status.ts`).
      confirmation: 'Confirmation demandée',
      // An open top-up whose direct debit the bank is collecting (`status.ts`).
      collecting: 'Prélèvement en cours',
    },
    // The detail panel of one top-up (`components/PurchaseDetail.vue`).
    detail: {
      open: 'Détails',
      title: 'Recharge du {date}',
      kinds: {
        credit_top_up: 'Recharge',
        credit_auto_top_up: 'Auto-recharge',
      },
      paid: 'Payé (TTC)',
      bonus: 'dont bonus de recharge',
      failedAt: 'le {date}',
      documents: 'Documents',
      invoice: 'Facture',
      noInvoice: 'La facture est émise une fois le paiement encaissé.',
      creditNote: 'Avoir',
      creditNotes: 'Les avoirs de ses remboursements sont dans vos factures.',
      seeDocuments: 'Voir les factures',
      refunds: 'Remboursements',
      refundStatus: {
        requested: 'Demandé',
        succeeded: 'Remboursé',
        failed: 'Échoué',
      },
      reclaimed: '{amount} de crédits retirés, bonus compris au prorata.',
      refundFailed: 'Échec : {reason}',
      close: 'Fermer',
    },
    recap: {
      reference: 'Référence',
      status: 'État',
      amountExclTax: 'Montant HT',
      tax: 'TVA',
      credited: 'Crédité',
      charged: 'À payer (TTC)',
      neverOpened:
        'Cette recharge n’a jamais atteint le service de facturation : rien n’a été débité et elle se ferme à son échéance.',
      lastFailure: 'Dernier échec de paiement : {reason}',
      confirmationNote: 'Votre banque demande de confirmer ce paiement : reprenez-le avec « Payer » pour le valider.',
    },
    failureReasons: {
      insufficient_funds: 'provision insuffisante',
      instrument_unusable: 'moyen de paiement inutilisable (expiré ou bloqué)',
      authentication_required: 'votre banque demande que vous confirmiez le paiement',
      refused: 'paiement refusé par la banque',
      cancelled_by_customer: 'paiement annulé',
      provider_unavailable: 'service de paiement momentanément indisponible',
      unknown: 'raison non précisée',
    },
  },
  // The cost disclosures, the price catalogue and the home card's wallet label.
  title: 'Facturation',
  wallet: {
    // 10.8: a negative balance is storage and delivery that kept running at zero.
    debt: 'Ce solde négatif vient du stockage et de la diffusion, qui continuent d\'être facturés à solde vide, jusqu\'à {ceiling} de découvert. La prochaine recharge le règle en premier.',
    debtNoCeiling: 'Ce solde négatif vient du stockage et de la diffusion, qui continuent d\'être facturés à solde vide. La prochaine recharge le règle en premier.',
    title: 'Portefeuille',
    // The credits of a top-up whose direct debit is being collected (`incoming_cents`).
    incoming: '+ {amount} en attente de confirmation du prélèvement',
    details: 'Détails',
    // The home card's two figures for the month (`CreditsCard.vue`).
    spentLabel: 'Dépensé ce mois',
    projectionLabel: 'Fin de mois estimée',
    available: 'Disponible',
    balanceLabel: 'Solde du portefeuille',
    monthlyFree: '{amount}/mois offerts',
    lowHint: 'Votre solde commence à baisser — rechargez pour éviter toute interruption.',
    criticalHint: 'Votre solde est presque épuisé — rechargez maintenant pour garder vos services actifs.',
    depletedHint: 'Votre solde est épuisé — vos liens de partage, intégrations et votre site sont hors-ligne jusqu\'à recharge.',
  },
  recurring: {
    monthlyTotal: 'Coût mensuel',
    perMonth: '/mois',
    manage: 'Gérer',
    kinds: {
      channel: 'Chaîne',
      custom_domain: 'Domaine',
    },
  },
  // The wallet explainer (6.1, `modals/WalletExplainerModal.vue`). Every figure is a parameter:
  // the grant is the wallet read's, the bonus the platform's tiers, the rest the catalogue's.
  explainer: {
    title: 'Vos crédits, simplement',
    lede: '1 crédit = 1 € HT. Pas d\'abonnement : vous payez ce que vous utilisez.',
    free: {
      label: 'Offerts',
      value: '{amount} chaque mois',
      text: 'Versés à chaque date anniversaire de votre organisation. Ils ne se cumulent pas : ceux du mois remplacent ceux du mois précédent.',
      next: 'Prochains : le {date}.',
      none: 'Aucun',
      noneText: 'Cette organisation ne reçoit pas de crédits offerts : seule la première organisation d\'une personne en reçoit.',
    },
    bought: {
      label: 'Achetés',
      value: 'N\'expirent jamais',
      bonus: 'Jusqu\'à +{percent} % de bonus selon le montant.',
      auto: 'Vous pouvez aussi recharger automatiquement sous un seuil.',
    },
    buys: {
      title: 'Ce que permettent {amount}',
      video: '{n} heures de vidéo stockées pendant un mois',
      subtitles: '{n} minutes de sous-titres, par langue',
      delivery: '{n} Go de diffusion au-delà des {free} offerts chaque mois',
      deliveryNeverPaid: '{n} Go de diffusion ; {free} offerts chaque mois à partir du mois qui suit votre première recharge',
      channel: '{n} mois de chaîne ou de domaine personnalisé',
      // Where a domain is not priced like a channel, and where videos are billed by their sources.
      deliveryNoAllowance: '{n} Go de diffusion',
      channelOnly: '{n} mois de chaîne',
      domain: '{n} mois de domaine personnalisé',
      stored: '{n} Go de vidéos et de fichiers stockés pendant un mois',
      // A price of zero: `{service}` is `pricing.skus`' name of it.
      offered: '{service} : offert',
    },
    empty: {
      goesOnLabel: 'À solde vide, continue',
      goesOn: 'Votre médiathèque, la lecture par votre équipe. Le stockage et la diffusion restent facturés, et le solde passe en négatif.',
      stopsLabel: 'À solde vide, s\'arrête',
      stops: 'Partages, intégrations et chaînes pour vos visiteurs ; nouveaux imports, dont le premier mois de stockage est débité à l’envoi ; nouveaux sous-titres.',
    },
    pricing: 'Tous les tarifs',
    later: 'Plus tard',
  },
  runway: {
    caption: 'à ce rythme',
    monthsValue: '≈ {n} mois',
    daysValue: '≈ {n} jours',
  },
  // The home card « Ce mois-ci » (5.3, `apps/home/components/CreditsCard.vue`), and the month-end
  // projection the wallet read serves (5.5).
  month: {
    projection: 'Projection fin de mois ≈ {amount}',
    projectionShort: 'Fin de mois ≈ {amount}',
    spentLine: 'Ce mois-ci : {amount} dépensés',
    projectionHint: 'Dépensé ce mois-ci, plus le rythme de consommation actuel sur les jours restants, plus les frais mensuels encore dus ce mois-ci.',
    explain: 'Comprendre le portefeuille',
    title: 'Ce mois-ci',
    details: 'Consommation détaillée',
    balance: 'Solde',
    spent: 'Dépensé depuis le 1er, HT',
    runway: 'Autonomie à ce rythme',
    delivery: 'Diffusion offerte',
    deliveryValue: '{used} sur {included}',
    deliveryNone: 'Rien diffusé ce mois-ci',
  },
  // What storage costs a month, estimated (5.2 and 10.6, `storageCost.ts`).
  storageCost: {
    cardLabel: 'Coût estimé',
    cardHint: 'Vidéos à {videoRate}, autres fichiers à {fileRate}. Décompté chaque jour sur vos crédits.',
    // An organization whose videos are billed by their sources' bytes (`useMonthlyEstimate`).
    cardHintBytes: 'Vidéos et autres fichiers à {fileRate}, une vidéo au Go de sa source. Décompté chaque jour sur vos crédits.',
  },
  uploadCost: {
    // The upload dialog's line (`CreateNewAssetModal.vue`), before any file is chosen.
    firstMonth: 'Le premier mois de stockage est débité à l’envoi et n’est pas remboursé : {fileRate} pour un fichier, d’après sa taille ; {videoRate} pour une vidéo, d’après sa durée, mesurée une fois l’envoi terminé.',
    firstMonthBytes: 'Le premier mois de stockage est débité à l’envoi et n’est pas remboursé : {fileRate} pour un fichier ou une vidéo, d’après sa taille.',
  },
  // The balance banners of the app shell (`components/BalanceBanners.vue`, `banners.ts`). The
  // member's version names no amount and offers no top-up: the wallet is not theirs to read (U6).
  banners: {
    low: 'Il vous reste environ {days} jours de crédits, au rythme de {rate} par jour.',
    critical: {
      title: 'Moins d’un jour de crédits. | Plus qu’un jour de crédits. | Plus que {n} jours de crédits.',
      text: 'À zéro, vos partages, embeds et chaînes seront hors ligne pour vos visiteurs.',
    },
    empty: {
      title: 'Votre solde est épuisé.',
      text: 'Vos partages, embeds et chaînes sont hors ligne pour vos visiteurs, et l’import de nouveaux fichiers est suspendu ; votre médiathèque reste accessible.',
    },
    // A wallet that has never paid, held to its daily allowance of public delivery while credit
    // remains (anti-abuse decision 10): it reopens at the day's turn, and a first top-up lifts it.
    dailyAllowance: {
      title: 'Diffusion publique suspendue jusqu’à demain.',
      text: 'Votre organisation n’a encore jamais rechargé son crédit, et elle a atteint aujourd’hui sa limite de {allowance} de diffusion par jour : vos partages, embeds et chaînes sont hors ligne pour vos visiteurs jusqu’à demain. Une première recharge lève cette limite.',
      // The same, when the server names no figure.
      textUnquoted: 'Votre organisation n’a encore jamais rechargé son crédit, et elle a atteint aujourd’hui sa limite de diffusion du jour : vos partages, embeds et chaînes sont hors ligne pour vos visiteurs jusqu’à demain. Une première recharge lève cette limite.',
    },
    deletion: {
      text: 'Sans recharge, vos contenus pourront être supprimés à partir du {date}.',
      action: 'Recharger pour les conserver',
    },
    confirmation: {
      text: 'Votre banque demande de confirmer l’auto-recharge de {amount}.',
      action: 'Confirmer le paiement',
    },
    autoTopUpOff: {
      text: 'L’auto-recharge est désactivée : votre carte a été refusée {n} fois de suite.',
      action: 'Réactiver l’auto-recharge',
    },
    member: {
      text: 'Les partages, embeds et chaînes de l’organisation sont momentanément hors ligne pour vos visiteurs. Votre médiathèque reste accessible.',
      dailyAllowance: 'Les partages, embeds et chaînes de l’organisation sont hors ligne pour vos visiteurs jusqu’à demain : l’organisation, qui n’a encore jamais rechargé son crédit, a atteint sa limite de diffusion du jour. Une première recharge la lève ; votre médiathèque reste accessible.',
      contact: 'Contactez un responsable de la facturation.',
    },
    // The month's free delivery (5.1), from `usage-periods`: once a month each, masquables.
    deliveryNear: {
      text: 'Vous avez utilisé {used} de votre {included} de diffusion offert ce mois-ci. Au-delà : {rate}.',
    },
    deliveryExceeded: {
      text: 'Le {included} de diffusion offert ce mois-ci est dépassé : {billed} facturés à ce jour, soit {amount}.',
    },
    seeUsage: 'Voir la consommation',
    autoTopUp: 'Auto-recharge',
    dismiss: 'Masquer',
    // Beside critical, empty and deletion while a direct debit is being collected (`incoming_cents`,
    // which counts the bonus): the credits are named, not the payment's amount.
    incoming: 'Un paiement est en cours de prélèvement : {amount} de crédits arrivent dès sa confirmation.',
  },
  activate: {
    // Le mot « abonnement » est celui qui a été retenu : il dit la récurrence
    // sans décrire un prélèvement daté, que rien ne garantit aujourd'hui.
    channelCost: 'Publier une chaîne, c\'est un abonnement à {amount}/mois.',
    // A channel fee negotiated to zero (`ChannelBillingSection.vue`).
    channelOffered: 'Votre chaîne vous est offerte : aucun abonnement n’est prélevé pour elle.',
    // « Ce nom de domaine coûte X/mois » se lisait comme un tarif d'achat : c'est
    // le prix que pratique un bureau d'enregistrement. On facture le
    // raccordement d'une adresse, jamais l'adresse elle-même.
    domainLabel: 'Raccorder une adresse que vous possédez déjà',
    domainDetail: 'En crédits, en plus de la chaîne',
    creditsNote: 'Vous pouvez remettre la chaîne hors ligne quand vous voulez : l\'abonnement s\'arrête.',
    domainNote: 'Videas ne vend pas de noms de domaine : achetez le vôtre chez un bureau d\'enregistrement, puis raccordez-le ici. Débité chaque mois de votre portefeuille.',
  },
  // The subtitles' cost line (`components/SubtitlesCostDisclosure.vue`): the amount is the line's,
  // the detail says the quantity and the rate it comes from, so the reader can multiply them.
  subtitlesCost: {
    label: 'Sous-titres générés',
    // vue-i18n's plural: « aucune | une | plusieurs ».
    languages: 'aucune langue | {n} langue | {n} langues',
    minutes: '{count} min commencées',
    formula: '{languages} × {minutes} × {rate}',
    roundedUp: 'Chaque langue est arrondie au centime supérieur.',
    free: 'Gratuit',
    note: 'Débité de votre portefeuille quand la génération aboutit.',
    correctionOnly: 'Une correction ne génère aucune langue : elle est gratuite.',
    correctionFree: 'Corriger ensuite est gratuit.',
    corrections: '{languages} : correction de la transcription, gratuite.',
    speechUnknown: 'Si l’une de ces langues est celle parlée dans la vidéo, elle sera corrigée gratuitement.',
    // With no transcript, the price supposes the language spoken is among those chosen.
    spokenAssumed:
      'Le prix suppose que la vidéo est dans cette langue ; sinon, une langue de plus sera débitée. | Le prix suppose que la vidéo est dans l’une de ces langues ; sinon, une langue de plus sera débitée.',
    // The same, when the language supposed heard is asked in several variants, all free with it.
    spokenAssumedGroup:
      'Le prix suppose que la vidéo est en {language}, dont {languages} sont alors des corrections gratuites ; sinon, jusqu’à {count} langues de plus seront débitées.',
    // Sans durée connue, un total serait une promesse en l'air : on s'en tient au tarif.
    rateOnly: 'Durée pas encore connue · {languages}',
    rateOnlyNote: 'Le coût exact sera calculé à la fin, sur la durée réelle de la vidéo.',
    // Subtitles offered to the organization: no formula, nothing debited.
    offered: '{languages} · le sous-titrage vous est offert',
  },
  pricing: {
    free: '{amount} de crédits offerts chaque mois',
    recurringTitle: 'Coûts récurrents (mensuel)',
    usageTitle: 'À l\'usage',
    bonusTitle: 'Bonus de recharge',
    bonusHint: 'Plus vous rechargez, plus vous gagnez de crédits bonus.',
    commission: 'Quand vous monétisez une chaîne, Videas prélève {percent}% de commission sur vos revenus.',
    skus: {
      channels: 'Chaîne',
      custom_domain: 'Nom de domaine',
      video_storage: 'Stockage vidéo',
      file_storage: 'Stockage fichiers',
      bandwidth: 'Bande passante',
      subtitles: 'Sous-titrage IA',
    },
    units: {
      channels: '/mois',
      custom_domain: '/mois',
      video_storage: '/heure/mois',
      file_storage: '/Go/mois',
      bandwidth: '/Go',
      subtitles: '/minute',
    },
  },
  // The organization's prices (`components/OrgPriceList.vue`): its own where negotiated, with the
  // day they end and what follows (`docs/plans/org-pricing.md` OP7).
  prices: {
    title: 'Vos tarifs',
    subtitle: 'Les prix HT appliqués à votre organisation aujourd’hui.',
    negotiated: 'Tarif négocié',
    until: 'jusqu’au {date} inclus',
    nextNegotiated: 'À partir du {date} : {price}, tarif négocié',
    nextPublic: 'À partir du {date} : {price}, tarif public',
    allowance: '{size} offerts chaque mois',
    noAllowance: 'Facturée dès le premier Go',
    videoBySources: 'Au Go de leurs sources',
    videoBySourcesDetail: 'Vos vidéos sont facturées au Go de leurs sources, au tarif du stockage fichiers ; leurs versions encodées restent incluses.',
    videoByHoursFrom: 'À l’heure à partir du {date}',
    videoBySourcesFrom: 'Au Go de leurs sources à partir du {date}',
  },
}
